import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Context } from '../../context/appContext';
import { apiFetch } from '../../hooks/useAuth';

/**
 * Custom hook to manage punchout cart state and synchronization.
 * Handles merging with global app cart, persistence to backend,
 * and provides cart manipulation methods.
 *
 * @param {Object} options
 * @param {string} options.sessionId - Punchout session ID for backend persistence
 * @returns {Object} Cart state and controls
 */
export function usePunchoutCart({ sessionId = null } = {}) {
    const { state: appState } = useContext(Context);
    const [cart, setCart] = useState({});
    const [persistError, setPersistError] = useState(null);
    
    // Track if initial sync from appState has been done
    const initialSyncDone = useRef(false);

    /**
     * Persist cart to backend with session guard.
     * Only persists if sessionId is available.
     */
    const persistCart = useCallback(async (cartData) => {
        if (!sessionId) {
            return; // Can't persist without session
        }

        try {
            const response = await apiFetch('/app/punchout/cart', {
                method: 'PUT',
                body: JSON.stringify({
                    sessionId,
                    cart: cartData
                })
            });

            if (!response.ok) {
                throw new Error('Failed to update punchout cart');
            }
            
            setPersistError(null);
        } catch (err) {
            console.warn('Failed to sync cart to server:', err);
            setPersistError(err.message);
        }
    }, [sessionId]);

    /**
     * Sync cart from existing punchout session data.
     * Called when session is loaded and has cart data.
     */
    const syncFromSession = useCallback((sessionCart) => {
        if (sessionCart && Object.keys(sessionCart).length > 0) {
            setCart(prev => ({
                ...prev,
                ...sessionCart
            }));
        }
    }, []);

    // Initial sync from appState.cart on mount (one-time)
    useEffect(() => {
        if (!initialSyncDone.current && appState?.cart && Object.keys(appState.cart).length > 0) {
            setCart(appState.cart);
            initialSyncDone.current = true;
        }
    }, [appState?.cart]);

    // Watch for changes to the regular cart and sync them to punchout cart + backend
    // Uses functional update to avoid stale closure issues
    useEffect(() => {
        if (appState?.cart && sessionId && initialSyncDone.current) {
            setCart(prevCart => {
                const mergedCart = {
                    ...prevCart,
                    ...appState.cart
                };
                
                // Persist merged cart to backend
                persistCart(mergedCart);
                
                return mergedCart;
            });
        }
    }, [appState?.cart, sessionId, persistCart]);

    /**
     * Add item to cart or increment quantity if exists.
     */
    const addToCart = useCallback(async (item) => {
        const updatedCart = await new Promise(resolve => {
            setCart(prev => {
                const newCart = {
                    ...prev,
                    [item.isbn]: {
                        ...item,
                        qty: (prev[item.isbn]?.qty || 0) + 1
                    }
                };
                resolve(newCart);
                return newCart;
            });
        });
        
        await persistCart(updatedCart);
    }, [persistCart]);

    /**
     * Remove item from cart by ISBN.
     */
    const removeFromCart = useCallback(async (isbn) => {
        const updatedCart = await new Promise(resolve => {
            setCart(prev => {
                const newCart = { ...prev };
                delete newCart[isbn];
                resolve(newCart);
                return newCart;
            });
        });
        
        await persistCart(updatedCart);
    }, [persistCart]);

    /**
     * Update quantity for an item. Removes item if qty <= 0.
     */
    const updateQuantity = useCallback(async (isbn, qty) => {
        const updatedCart = await new Promise(resolve => {
            setCart(prev => {
                const newCart = { ...prev };
                if (qty <= 0) {
                    delete newCart[isbn];
                } else {
                    newCart[isbn] = {
                        ...newCart[isbn],
                        qty
                    };
                }
                resolve(newCart);
                return newCart;
            });
        });
        
        await persistCart(updatedCart);
    }, [persistCart]);

    /**
     * Clear the cart entirely.
     */
    const clearCart = useCallback(() => {
        setCart({});
    }, []);

    /**
     * Calculate total price of all items in cart.
     */
    const calculateTotal = useCallback(() => {
        return Object.values(cart).reduce((total, item) => {
            return total + (item.price * item.qty);
        }, 0);
    }, [cart]);

    /**
     * Format price as EUR currency.
     */
    const formatPrice = useCallback((price) => {
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR'
        }).format(price);
    }, []);

    return {
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        syncFromSession,
        calculateTotal,
        formatPrice,
        persistError,
        itemCount: Object.keys(cart).length,
        total: calculateTotal()
    };
}
